import { FC } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Grid } from '@mui/material';

import { FieldTableRow } from 'components/_basic/Form/FieldTable2/components/FieldTableRow';
import { ReadOnlyTableRow } from 'components/BusinessToolForm/Sections/FinancialPlan/components/TableComponents/ReadOnlyTableRow';
import { TableSectionTitle } from 'components/BusinessToolForm/Sections/FinancialPlan/components/TableComponents/TableSectionTitle';
import { TotalRowComponent } from 'components/BusinessToolForm/Sections/FinancialPlan/components/TableComponents/TotalRowComponent';
import { useUnitChange } from 'components/BusinessToolForm/Sections/FinancialPlan/hooks/useUnitChange';
import { getEachRowValues } from 'components/BusinessToolForm/Sections/FinancialPlan/ProfitAndLossProjection/components/FormSections/NetSalesForm/helpers';
import { GLOBAL_PERIOD_NAME } from 'components/BusinessToolForm/Sections/Global/form';

import { getPeriodColumnsNames } from 'helpers/businessTool';
import { concatWithDot } from 'helpers/string';

import { IFinancingActivitiesProps } from './types';
import { FINANCING_ACTIVITIES_NAMES, useGetFinancingActivitiesTableHeadings } from './utils/form';
import { financingActivitiesPrefix, FIRST_COLUMN_WIDTH } from '../../../../utils/form';

export const FinancingActivities: FC<IFinancingActivitiesProps> = ({ disabled }) => {
  const { t } = useTranslation();

  const { control, getValues } = useFormContext();

  const { key } = useUnitChange();
  const { financingActivitiesLinkedFields, financingActivitiesVariants, totalRowTitle } =
    useGetFinancingActivitiesTableHeadings();

  const period = useWatch({ name: GLOBAL_PERIOD_NAME });

  const tableName = concatWithDot(financingActivitiesPrefix, FINANCING_ACTIVITIES_NAMES.TABLE);

  const { fields } = useFieldArray({ control, name: tableName });

  const columnsNames = getPeriodColumnsNames(period);

  const totalName = concatWithDot(financingActivitiesPrefix, FINANCING_ACTIVITIES_NAMES.TOTAL);

  const issuanceDebt = getValues(
    concatWithDot(financingActivitiesPrefix, FINANCING_ACTIVITIES_NAMES.PROCEEDS_ISSUANCE_DEBT),
  );
  const repaymentDebt = getValues(concatWithDot(financingActivitiesPrefix, FINANCING_ACTIVITIES_NAMES.REPAYMENT_DEBT));
  const issuanceEquity = getValues(
    concatWithDot(financingActivitiesPrefix, FINANCING_ACTIVITIES_NAMES.PROCEEDS_ISSUANCE_EQUITY),
  );
  const financialNetCash = getValues(totalName);

  return (
    <Grid container direction={'column'}>
      <Grid item xs>
        <TableSectionTitle title={t('financialPlan.projectedCashFlow.direct.financingActivities.title')} />
      </Grid>
      <Grid item xs>
        <ReadOnlyTableRow
          name={concatWithDot(financingActivitiesPrefix, FINANCING_ACTIVITIES_NAMES.PROCEEDS_ISSUANCE_DEBT)}
          columnValues={getEachRowValues([issuanceDebt], 0, period)}
          firstColumnWidth={FIRST_COLUMN_WIDTH}
          rowTitle={financingActivitiesLinkedFields[FINANCING_ACTIVITIES_NAMES.PROCEEDS_ISSUANCE_DEBT].title}
          noNeedName={true}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs>
        <ReadOnlyTableRow
          name={concatWithDot(financingActivitiesPrefix, FINANCING_ACTIVITIES_NAMES.REPAYMENT_DEBT)}
          columnValues={getEachRowValues([repaymentDebt], 0, period)}
          firstColumnWidth={FIRST_COLUMN_WIDTH}
          rowTitle={financingActivitiesLinkedFields[FINANCING_ACTIVITIES_NAMES.REPAYMENT_DEBT].title}
          noNeedName={true}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs>
        <ReadOnlyTableRow
          name={concatWithDot(financingActivitiesPrefix, FINANCING_ACTIVITIES_NAMES.PROCEEDS_ISSUANCE_EQUITY)}
          columnValues={getEachRowValues([issuanceEquity], 0, period)}
          firstColumnWidth={FIRST_COLUMN_WIDTH}
          rowTitle={financingActivitiesLinkedFields[FINANCING_ACTIVITIES_NAMES.PROCEEDS_ISSUANCE_EQUITY].title}
          noNeedName={true}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs key={key}>
        {fields.map((rowItem, index) => (
          <FieldTableRow
            key={rowItem.id}
            name={tableName}
            rowIndex={index}
            columnsNames={columnsNames || []}
            firstColumnWidth={FIRST_COLUMN_WIDTH}
            disabled={disabled}
            rowTitle={financingActivitiesVariants[index]}
            dataAttributeWithIndex
            needToConvertToNegative
          />
        ))}
        <TotalRowComponent
          name={totalName}
          columnValues={getEachRowValues([financialNetCash], 0, period)}
          firstColumnWidth={FIRST_COLUMN_WIDTH}
          totalRowTitle={totalRowTitle}
          disabled={disabled}
          marginBottomSize="none"
        />
      </Grid>
    </Grid>
  );
};
